import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useOrganization } from '@/domains/organizations';
import { useCampaigns } from '@/domains/campaigns';
import { brandRepository } from '../repositories/brand.repository';
import { brandService } from '../services/brand.service';
import { brandKeys } from '../services/brand.keys';
import type { BrandMetrics } from '../types/brand.types';

export type { BrandMetrics };

export function useBrandMetrics(brandId: string, brandName?: string) {
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.id;
  const { data: campaigns, isLoading: campaignsLoading } = useCampaigns();

  const query = useQuery({
    queryKey: brandKeys.metrics(orgId, brandId),
    queryFn: async (): Promise<BrandMetrics> => {
      if (!orgId) {
        throw new Error('No current organization selected');
      }
      return brandRepository.getBrandMetrics(orgId, brandId);
    },
    enabled: !!brandId && !!orgId,
    staleTime: 3 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const data = useMemo(() => {
    if (!query.data) return undefined;
    if (campaigns && (brandId || brandName)) {
      return brandService.overlayCampaignMetrics(query.data, campaigns, brandId, brandName);
    }
    return query.data;
  }, [query.data, campaigns, brandId, brandName]);

  return {
    ...query,
    data,
    isLoading: query.isLoading || (campaignsLoading && !data),
  };
}
